import i18n from "../../../../../utils/i18n";
import classes from "./consultationInterstitialLayout.module.scss";

// Local `Radio` sub-component, duplicated per the established convention in
// this codebase (see CommonFillingMethod.jsx, CommonCompanyType.jsx,
// CommonMoreBranch.jsx) — centralized here instead since every consultation
// interstitial step is a thin wrapper around this one shared layout.
const Radio = ({ checked }) => (
  <span
    className={`${classes.radio} ${checked ? classes.radioChecked : ""}`}
    aria-hidden="true"
  />
);

/**
 * Generic "continue vs. book a consultation" interstitial layout, shared by
 * CommonKftInterstitial, CommonKftCegjelzoInterstitial and
 * NewMultiCompanyInterstitial. Structurally identical to CommonFillingMethod
 * (icon + recommended badge + description two-card layout) — copied verbatim
 * and parameterized over `options` instead of a hardcoded list.
 *
 * Props:
 * - titleKey: i18n key for the header title (required)
 * - subtitleKey: i18n key for the header subtitle (optional)
 * - options: [{ value, titleKey, descKey, Icon, recommended }] — `recommended`
 *   only controls the visual "Recommended" badge, nothing functional.
 * - selection: currently selected option value
 * - onSelectionChange: (value) => void
 */
const ConsultationInterstitialLayout = ({
  titleKey,
  subtitleKey,
  options,
  selection,
  onSelectionChange,
}) => {
  return (
    <div className={classes.root}>
      <div className={classes.scrollArea}>
        <div className={classes.content}>
          <div className={classes.mainColumn}>
            <header className={classes.header}>
              <h1 className={classes.title}>{i18n.t(titleKey)}</h1>
              {subtitleKey ? (
                <p className={classes.subtitle}>{i18n.t(subtitleKey)}</p>
              ) : null}
            </header>

            <div className={classes.rightColumn}>
              <div className={classes.cardsContainer}>
                {options.map((option) => {
                  const isSelected = selection === option.value;
                  const { Icon } = option;

                  return (
                    <div key={option.value} className={classes.cardWrapper}>
                      <button
                        type="button"
                        className={`${classes.card} ${isSelected ? classes.selectedRow : ""}`}
                        onClick={() => onSelectionChange(option.value)}
                        aria-pressed={isSelected}
                      >
                        {option.recommended && (
                          <span className={classes.recommendedBadge}>
                            {i18n.t("searchflow.consultationrecommendedbadge")}
                          </span>
                        )}

                        <div className={classes.cardInner}>
                          <div className={classes.cardHeader}>
                            <div
                              className={`${classes.iconCircle} ${option.value === "consultation" ? classes.iconCircleAccent : ""}`}
                            >
                              <Icon className={classes.cardIcon} aria-hidden="true" />
                            </div>
                            <div className={classes.cardHeaderText}>
                              <div className={classes.cardTitle}>
                                {i18n.t(option.titleKey)}
                              </div>
                            </div>
                            <Radio checked={isSelected} />
                          </div>

                          <div className={classes.cardDivider} aria-hidden="true" />

                          <p className={classes.cardDescription}>
                            {i18n.t(option.descKey)}
                          </p>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationInterstitialLayout;
